import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getFinoviewCompanies, importarFinoview, exportarFinoview } from "@/lib/api";
import { notifyDataChanged } from "@/lib/events";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  ArrowLeft,
  ArrowRight,
  Plug,
  KeyRound,
  Building2,
  CheckCircle2,
  AlertCircle,
  Download,
  Upload,
} from "lucide-react";

const ALL_YEARS = "__all__";

export default function ConectarFinoviewPage() {
  const { empresaId } = useParams();
  const navigate = useNavigate();

  const [apiKey, setApiKey] = useState("");
  const [step, setStep] = useState(1); // 1=conectar · 2=elegir empresa/año · 3=resultado

  const [connecting, setConnecting] = useState(false);
  const [importing, setImporting] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedYear, setSelectedYear] = useState(ALL_YEARS);
  const [result, setResult] = useState(null);

  // Exportación (FinReport → Finoview). Key con escritura, distinta de la de import.
  const [exportKey, setExportKey] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);

  // Paso 1: conectar y listar empresas de Finoview
  const handleConnect = async () => {
    if (!apiKey.trim()) {
      toast.error("Introduce la API key de Finoview");
      return;
    }
    setConnecting(true);
    try {
      const { data } = await getFinoviewCompanies(apiKey.trim());
      // El backend responde { data: [...] }; aceptamos también companies/array por robustez.
      const lista = Array.isArray(data)
        ? data
        : data?.data || data?.companies || [];
      setCompanies(lista);
      setStep(2);
      if (lista.length === 0) {
        toast.message("Conectado, pero no se encontraron empresas en Finoview");
      } else {
        toast.success(`Conectado · ${lista.length} empresas disponibles`);
      }
    } catch (e) {
      // 401/404/429 vienen como mensaje del backend
      toast.error(
        e.response?.data?.detail || "No se pudo conectar con Finoview. Revisa la API key."
      );
    } finally {
      setConnecting(false);
    }
  };

  // Selección de una empresa de la lista
  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setSelectedYear(ALL_YEARS);
  };

  // Paso 2 → 3: importar
  const handleImport = async () => {
    if (!selectedCompany) {
      toast.error("Selecciona una empresa de Finoview");
      return;
    }
    setImporting(true);
    try {
      const payload = {
        api_key: apiKey.trim(),
        finoview_company_id:
          selectedCompany.id ?? selectedCompany.companyId ?? selectedCompany.finoview_company_id,
        // "Todos los ejercicios" = omitir year (el backend espera Optional[int];
        // mandar el texto "todos" provocaba un 422).
        ...(selectedYear === ALL_YEARS ? {} : { year: parseInt(selectedYear, 10) }),
      };
      const { data } = await importarFinoview(empresaId, payload);
      setResult(data);
      setStep(3);
      toast.success("Importación desde Finoview completada");
      // El sidebar muestra los ejercicios con datos; refrescarlo tras importar.
      notifyDataChanged("ejercicios");
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Error al importar desde Finoview"
      );
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setStep(1);
    setCompanies([]);
    setSelectedCompany(null);
    setSelectedYear(ALL_YEARS);
    setResult(null);
  };

  // Exportar ESTA empresa de FinReport a Finoview (bring-your-own-key con escritura).
  const handleExport = async () => {
    if (!exportKey.trim()) {
      toast.error("Pega tu API key de Finoview con permiso de escritura");
      return;
    }
    setExporting(true);
    setExportResult(null);
    try {
      const { data } = await exportarFinoview(empresaId, { api_key: exportKey.trim() });
      setExportResult(data);
      toast.success("Empresa exportada a Finoview");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al exportar a Finoview");
    } finally {
      setExporting(false);
    }
  };

  const companyId = (c) => c?.id ?? c?.companyId ?? c?.finoview_company_id;
  const ejerciciosCreados = result?.ejercicios || result?.ejercicios_creados || [];

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="conectar-finoview-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/empresas/${empresaId}`)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Plug className="h-7 w-7 text-primary" />
            Conectar Finoview
          </h1>
          <p className="text-muted-foreground mt-1">
            Importa empresas desde Finoview (sin Excel) o exporta esta empresa a tu
            cuenta de Finoview.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-sm">
        {["Conectar", "Elegir empresa", "Resultado"].map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : done
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <span className="font-mono font-bold">{done ? "✓" : n}</span>
                <span className="font-medium">{label}</span>
              </div>
              {n < 3 && <span className="text-muted-foreground">→</span>}
            </div>
          );
        })}
      </div>

      {/* ───────────── PASO 1: CONECTAR ───────────── */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              API key de Finoview
            </CardTitle>
            <CardDescription>
              Pega tu API key de Finoview para listar las empresas disponibles en tu cuenta.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="finoview-api-key">API key</Label>
              <Input
                id="finoview-api-key"
                type="password"
                placeholder="Pega aquí tu API key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleConnect();
                }}
                data-testid="finoview-api-key-input"
              />
              <p className="text-xs text-muted-foreground">
                La clave solo se usa para esta conexión y no se almacena en el navegador.
              </p>
            </div>
            <Button
              className="w-full"
              onClick={handleConnect}
              disabled={connecting || !apiKey.trim()}
              data-testid="finoview-connect-btn"
            >
              {connecting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent mr-2" />
                  Conectando...
                </>
              ) : (
                <>
                  <Plug className="h-4 w-4 mr-2" />
                  Conectar
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ───────────── PASO 2: ELEGIR EMPRESA Y AÑO ───────────── */}
      {step === 2 && (
        <Card data-testid="finoview-companies-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Empresas en Finoview
            </CardTitle>
            <CardDescription>
              Selecciona la empresa que quieres importar y, si lo necesitas, el ejercicio concreto.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {companies.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No se encontraron empresas en esta cuenta de Finoview.</p>
              </div>
            ) : (
              <div className="rounded-md border max-h-96 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>CIF</TableHead>
                      <TableHead>Origen</TableHead>
                      <TableHead>Ejercicios</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companies.map((c) => {
                      const cid = companyId(c);
                      const selected = selectedCompany && companyId(selectedCompany) === cid;
                      const years = c.years || c.ejercicios || [];
                      return (
                        <TableRow
                          key={cid}
                          className={`cursor-pointer ${selected ? "bg-primary/10" : "hover:bg-muted/40"}`}
                          onClick={() => handleSelectCompany(c)}
                          data-testid={`finoview-company-${cid}`}
                        >
                          <TableCell>
                            <input
                              type="radio"
                              name="finoview-company"
                              checked={!!selected}
                              onChange={() => handleSelectCompany(c)}
                              className="h-4 w-4 accent-primary"
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {c.companyName || c.nombre || c.name || "—"}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{c.cif || "—"}</TableCell>
                          <TableCell>
                            {c.source ? (
                              <Badge variant="secondary" className="text-xs">
                                {c.source}
                              </Badge>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {years.length > 0 ? years.join(", ") : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Selector de año de la empresa elegida */}
            {selectedCompany && (
              <div className="space-y-1.5">
                <Label>Ejercicio a importar</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger data-testid="finoview-year-select">
                    <SelectValue placeholder="Elegir ejercicio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_YEARS}>Todos los ejercicios</SelectItem>
                    {(selectedCompany.years || selectedCompany.ejercicios || []).map((y) => (
                      <SelectItem key={String(y)} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Acciones */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={reset}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Otra API key
              </Button>
              <Button
                className="flex-1"
                onClick={handleImport}
                disabled={importing || !selectedCompany}
                data-testid="finoview-import-btn"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent mr-2" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Importar
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ───────────── PASO 3: RESULTADO ───────────── */}
      {step === 3 && result && (
        <Card className="animate-fade-in" data-testid="finoview-result-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Importación completada
            </CardTitle>
            <CardDescription>
              Los datos de Finoview se han importado en la empresa.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {ejerciciosCreados.length > 0 ? (
              <Alert className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertTitle className="text-emerald-700 dark:text-emerald-400">
                  {ejerciciosCreados.length}{" "}
                  {ejerciciosCreados.length === 1 ? "ejercicio creado" : "ejercicios creados"}
                </AlertTitle>
                <AlertDescription>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {ejerciciosCreados.map((ej, i) => (
                      <Badge key={i} variant="secondary" className="font-mono">
                        {ej.anio || ej.year || ej}
                        {ej.stats?.total != null ? ` · ${ej.stats.total} ctas` : ""}
                      </Badge>
                    ))}
                  </div>
                  {result.via && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Vía: {result.via === "trial-balance"
                        ? "Sumas y Saldos por cuenta (trial-balance)"
                        : "partidas agregadas (items)"}
                    </p>
                  )}
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/40">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-700 dark:text-amber-400">
                  Importación finalizada
                </AlertTitle>
                <AlertDescription>
                  {result.mensaje || result.message || "No se han devuelto ejercicios nuevos."}
                </AlertDescription>
              </Alert>
            )}

            {/* Verjas por ejercicio (descuadre, partidas sin mapear…) */}
            {ejerciciosCreados.some((ej) => (ej.avisos || []).length > 0) && (
              <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/40">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-700 dark:text-amber-400">Avisos</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4 space-y-1 text-xs">
                    {ejerciciosCreados.flatMap((ej) =>
                      (ej.avisos || []).map((a, i) => (
                        <li key={`${ej.anio}-${i}`}>
                          <span className="font-mono">{ej.anio}</span>: {a}
                        </li>
                      ))
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={reset}>
                Importar otra
              </Button>
              <Button
                className="flex-1"
                onClick={() => navigate(`/empresas/${empresaId}`)}
                data-testid="finoview-go-empresa-btn"
              >
                Ir a la empresa
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ───────────── EXPORTAR ESTA EMPRESA A FINOVIEW ───────────── */}
      <Card data-testid="finoview-export-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Exportar esta empresa a Finoview
          </CardTitle>
          <CardDescription>
            Envía las Sumas y Saldos de esta empresa a tu cuenta de Finoview. Pega tu
            API key <strong>con permiso de escritura</strong> (la generas en Finoview →
            Perfil → Desarrolladores / API).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="finoview-export-key">API key (con escritura)</Label>
            <Input
              id="finoview-export-key"
              type="password"
              placeholder="fv_live_… (con permiso de escritura)"
              value={exportKey}
              onChange={(e) => setExportKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleExport();
              }}
              data-testid="finoview-export-key-input"
            />
            <p className="text-xs text-muted-foreground">
              La key es tuya y por cliente; no se guarda. Reexportar actualiza, no
              duplica (idempotente por CIF + origen).
            </p>
          </div>
          <Button
            className="w-full"
            onClick={handleExport}
            disabled={exporting || !exportKey.trim()}
            data-testid="finoview-export-btn"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent mr-2" />
                Exportando...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Exportar a Finoview
              </>
            )}
          </Button>

          {exportResult && (
            <Alert className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertTitle className="text-emerald-700 dark:text-emerald-400">
                Exportación completada
              </AlertTitle>
              <AlertDescription className="space-y-2">
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="text-xs">Años exportados:</span>
                  {(exportResult.anios_exportados || []).map((y) => (
                    <Badge key={y} variant="secondary" className="font-mono">
                      {y}
                    </Badge>
                  ))}
                </div>
                {(exportResult.anios_omitidos_round_trip || []).length > 0 && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Omitidos (venían de Finoview, no se reexportan):{" "}
                    {exportResult.anios_omitidos_round_trip.join(", ")}
                  </p>
                )}
                {exportResult.empresa_sin_cif && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    ⚠️ Esta empresa no tiene CIF: Finoview no puede deduplicar, así que
                    reexportar la duplicaría. Añade el CIF en Gestión de empresa.
                  </p>
                )}
                {/* Validación que Finoview calcula sobre el dato ya escrito:
                    cuadre, resultado imputado y el detector de signos. */}
                {(exportResult.validacion || []).map((v) => (
                  <div key={v.anio} className="text-xs space-y-0.5">
                    <p>
                      <span className="font-mono font-semibold">{v.anio}</span>{" "}
                      {v.cuadrado
                        ? "· balance cuadrado en Finoview ✓"
                        : `· ⚠️ descuadre en Finoview: ${v.descuadre}`}
                      {v.resultado_imputado_a_pn != null &&
                        ` · resultado imputado al PN: ${v.resultado_imputado_a_pn}`}
                    </p>
                    {v.aviso_signos && (
                      <p className="text-amber-700 dark:text-amber-400">
                        ⚠️ Aviso de signos de Finoview: {v.aviso_signos}
                      </p>
                    )}
                    {(v.cuentas_no_mapeadas || []).length > 0 && (
                      <p className="text-amber-700 dark:text-amber-400">
                        Cuentas no mapeadas por Finoview ({v.cuentas_no_mapeadas.length}):{" "}
                        {v.cuentas_no_mapeadas.slice(0, 10).join(", ")}
                        {v.cuentas_no_mapeadas.length > 10 ? "…" : ""}
                      </p>
                    )}
                  </div>
                ))}
                {exportResult.finoview?.id && (
                  <p className="text-xs text-muted-foreground">
                    Finoview ID: <span className="font-mono">{exportResult.finoview.id}</span>
                    {exportResult.finoview.created === false && " (actualizada)"}
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
